import { supabase } from '@/integrations/supabase/client';
import { log } from '@/lib/logger';

/**
 * Deduplicates concurrent refreshSession() calls. Only one refresh runs at a
 * time — all concurrent callers share the same promise. This prevents auth
 * token lock contention ("lock stolen" errors from navigator.locks).
 */
let pendingRefresh: Promise<string> | null = null;

export function dedupedRefreshSession(): Promise<string> {
  if (pendingRefresh) return pendingRefresh;

  pendingRefresh = supabase.auth.refreshSession()
    .then(({ data, error: refreshError }) => {
      if (refreshError || !data.session) {
        log.warn('Token refresh failed', { message: refreshError?.message });
        throw new Error('Session expired. Please sign in again.');
      }
      return data.session.access_token;
    })
    .finally(() => { pendingRefresh = null; });

  return pendingRefresh;
}

/**
 * Get a fresh access token for direct fetch() calls that need a Bearer header.
 *
 * Uses getSession() first — if the token expires within 60 seconds, forces a
 * refresh via dedupedRefreshSession(). Safe alternative to getUser() which can
 * nuke valid sessions on transient 401 errors.
 *
 * Use this for streaming endpoints and any code that bypasses supabase.functions.invoke().
 */
export async function getRefreshedToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('No active session. Please sign in again.');
  }

  // If token expires within 60 seconds, proactively refresh
  const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
  if (expiresAt > 0 && Date.now() > expiresAt - 60_000) {
    return dedupedRefreshSession();
  }

  return session.access_token;
}
